import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNavbar from './PublicNavbar';
import PublicFooter from './PublicFooter';
import DarkSection from './DarkSection';
import { ScrollProgress, EASE, Eyebrow, KineticText, Reveal } from './motion';

// Shared shell for all public-facing pages: scroll progress bar,
// glass navbar, page fade-in, and dark footer.
const PublicLayout = ({ children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen overflow-x-clip bg-gray-50 font-sans text-gray-900 antialiased">
      <ScrollProgress />
      <PublicNavbar />
      <motion.main
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        {children}
      </motion.main>
      <PublicFooter />
    </div>
  );
};

// Dark hero header used by inner pages (About, Services, Candidates, Contact).
export const PageHero = ({ eyebrow, title, subtitle, children }) => (
  <DarkSection glow="top-right" className="pb-16 pt-32 md:pb-24 md:pt-44">
    {/* Subtle grid backdrop */}
    <div className="pointer-events-none absolute inset-0">
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
    </div>

    <div className="relative mx-auto max-w-7xl px-4 md:px-8">
      {eyebrow && (
        <Reveal y={16}>
          <Eyebrow dark>{eyebrow}</Eyebrow>
        </Reveal>
      )}
      <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl">
        <KineticText text={title} />
      </h1>
      {subtitle && (
        <Reveal delay={0.2} y={18}>
          <p className="mt-6 max-w-2xl text-lg text-gray-400 md:text-xl">{subtitle}</p>
        </Reveal>
      )}
      {children}
    </div>
  </DarkSection>
);

export default PublicLayout;
